
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Send, X, Bot, Loader2, MessageCircle } from 'lucide-react';
import { getTechnicalAdvice } from '../services/geminiService';
import { ChatMessage, Language, Translations } from '../types';
import { EditableText } from './EditableText';

interface GeminiAdvisorProps {
  currentLang: Language;
  t: Translations[Language];
  triggerSignal?: number; // Optional prop to trigger open from parent
}

export const GeminiAdvisor: React.FC<GeminiAdvisorProps> = ({ currentLang, t, triggerSignal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isRTL = currentLang === Language.AR;

  useEffect(() => {
    if (triggerSignal && triggerSignal > 0) {
      setIsOpen(true);
    }
  }, [triggerSignal]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, scrollToBottom]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg: ChatMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    setInput('');
    setLoading(true);

    try {
      const responseText = await getTechnicalAdvice(currentInput, currentLang, messages);
      const botMsg: ChatMessage = { role: 'model', text: responseText };
      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = useCallback(() => {
    const phone = t.contactInfo?.whatsapp?.replace(/[^0-9]/g, '');
    if (phone) {
        window.open(`https://wa.me/${phone}?text=Hello,%20I%20need%20assistance%20with%20MEITAI%20products`, '_blank');
    }
  }, [t.contactInfo?.whatsapp]);

  return (
    <>
      {/* Floating Action Button - Desktop Only */}
      <button
        onClick={() => setIsOpen(true)}
        className={`hidden lg:flex fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-xl transition-transform hover:scale-105 items-center gap-2`}
      >
        <Bot size={24} />
        <span className="font-semibold hidden sm:inline"><EditableText path="ai.title" lang={currentLang} /></span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed bottom-0 lg:bottom-24 ${isRTL ? 'left-0 lg:left-6' : 'right-0 lg:right-6'} w-full lg:w-96 h-[80vh] lg:h-[500px] bg-white lg:rounded-xl rounded-t-2xl shadow-2xl border-t lg:border border-gray-200 z-[60] flex flex-col overflow-hidden animate-in slide-in-from-bottom-5`}>
          {/* Header */}
          <div className="bg-primary-700 text-white p-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Bot size={20} />
              <h3 className="font-bold text-sm"><EditableText path="ai.title" lang={currentLang} /></h3>
            </div>
            
            <div className="flex items-center gap-2">
                <button 
                    onClick={handleWhatsApp}
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 transition-colors shadow-sm"
                    title="Chat on WhatsApp"
                >
                    <MessageCircle size={14} /> 
                    <span className="hidden xs:inline">WhatsApp</span>
                </button>
                <button onClick={() => setIsOpen(false)} className="hover:bg-primary-600 p-1 rounded">
                <X size={18} />
                </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-10 text-sm px-4">
                <Bot size={48} className="mx-auto mb-3 text-gray-300" />
                <p><EditableText path="ai.disclaimer" lang={currentLang} /></p>
                <button 
                   onClick={handleWhatsApp}
                   className="mt-6 text-primary-600 hover:text-primary-700 font-medium text-xs flex items-center justify-center gap-1 mx-auto border border-primary-200 bg-white px-3 py-2 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                >
                    <MessageCircle size={14} className="text-[#25D366]" /> Need human help? Chat on WhatsApp
                </button>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className={`max-w-[85%] rounded-lg p-3 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-primary-600 text-white rounded-tr-none' 
                      : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 p-3 rounded-lg rounded-tl-none shadow-sm">
                  <Loader2 size={16} className="animate-spin text-primary-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100 flex gap-2 pb-safe">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={t.ai.placeholder}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="auto"
            />
            <button 
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-primary-600 text-white p-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
